import React, { useState } from 'react';
import { Box, Grid, Typography, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import { validateOCRForm } from '../utils/validation';
import { 
    TextField,
    CustomCheckbox,
    CustomButton,
    CustomSelect
} from '../../../../v2/MUI_custom_component';
import { DatePicker } from '../../../../v2/MUI_custom_component';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";

const ReusableManualEntryForm = ({ 
    onSave, 
    onCancel, 
    initialData = {},
    title = "Enter Details Manually",
    // Validation options
    requirePhone = true,
    requireEmail = false,
    requireRelation = false,
    relations = [],
    minAge = 18,
    maxAge = 60,
    context = 'beneficiary',
    // Form configuration
    showGender = true,
    showAge = true,
    showConfirmation = true,
    confirmationText = "Details provided herein are true and I have obtained all relevant organisational consents for the purposes.",
    // Button configuration
    saveButtonText = "Save & Continue",
    cancelButtonText = "Cancel",
    showCancelButton = true,
    // Custom fields
    additionalFields = [],
    // Callbacks
    onFieldChange,
    customValidation
}) => {
    const [formData, setFormData] = useState({
        full_name: initialData.full_name || "",
        dob: initialData.dob || "",
        age: initialData.age || "",
        gender: initialData.gender || "MALE",
        phone_number: initialData.phone_number || "",
        email: initialData.email || "",
        relation: initialData.relation || "",
        ...initialData
    });
    
    const [validationErrors, setValidationErrors] = useState({});
    const [confirm, setConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const { [field]: removed, ...rest } = prev;
                return rest;
            });
        }

        // Auto-calculate age when DOB changes
        if (field === 'dob' && value) {
            const today = dayjs();
            const birthDate = dayjs(value);
            
            if (birthDate.isValid()) {
                const age = today.diff(birthDate, 'year');
                
                if (age >= 0) {
                    setFormData(prev => ({
                        ...prev,
                        age: age.toString()
                    }));
                }
            }
        }

        // Call custom field change handler if provided
        if (onFieldChange) {
            onFieldChange(field, value, formData);
        }
    };

    const validateForm = () => {
        // Use custom validation if provided, otherwise use default
        if (customValidation) {
            const validation = customValidation(formData, confirm);
            setValidationErrors(validation.errors || {});
            return validation.isValid;
        }

        const validation = validateOCRForm(formData, {
            requirePhone,
            requireEmail,
            requireRelation,
            relations,
            minAge,
            maxAge,
            context
        });

        let errors = { ...validation.errors };

        // Confirmation validation
        if (showConfirmation && !confirm) {
            errors.confirm = "Please confirm the details to proceed";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (isSubmitting) {
            return; // Prevent multiple submissions
        }

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);  
        try {
            await onSave({
                ...formData,
                beneficiary_id: initialData.beneficiary_id || Date.now(),
                ocr_verified: false // Manual entry, not OCR verified
            });
        } finally {
            setIsSubmitting(false);  
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    return (
        <Box>
            {title && (
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {title}
                </Typography>
            )}
            
            <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Full Name */}
                <Grid item xs={12}>
                    <TextField
                        label="Full Name*"
                        value={formData.full_name}
                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                        error={!!validationErrors.full_name}
                        helperText={validationErrors.full_name}
                        placeholder="Enter full name"
                        fullWidth
                    />
                </Grid>

                {/* Date of Birth */}
                <Grid item xs={12} sm={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date of Birth*"
                            value={formData.dob ? dayjs(formData.dob) : null}
                            onChange={(newValue) => {
                                const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
                                handleInputChange('dob', dateString);
                            }}
                            error={!!validationErrors.dob}
                            helperText={validationErrors.dob}
                            disableFuture
                            format="DD/MM/YYYY"
                            fullWidth
                        />
                    </LocalizationProvider>
                </Grid>

                {/* Age (if enabled) */}
                {showAge && (
                    <Grid item xs={12}>
                        <TextField
                            label="Age"
                            value={formData.age}
                            onChange={(e) => handleInputChange('age', e.target.value)}
                            error={!!validationErrors.age}
                            helperText={validationErrors.age}
                            placeholder="Age"
                            inputProps={{ min: 0, max: 120 }}
                            fullWidth
                            readOnly
                        />
                    </Grid>
                )}

                {/* Gender (if enabled) */}
                {showGender && (
                    <Grid item xs={12}>
                        <CustomSelect
                            id="gender-select"
                            label="Gender*"
                            value={formData.gender}
                            onChange={(e) => handleInputChange('gender', e.target.value)}
                            error={!!validationErrors.gender}
                            helperText={validationErrors.gender}
                            displayEmpty
                            fullWidth
                        >
                            <MenuItem value="" disabled>
                                Choose gender
                            </MenuItem>
                            <MenuItem value="MALE">Male</MenuItem>
                            <MenuItem value="FEMALE">Female</MenuItem>
                        </CustomSelect>
                    </Grid>
                )}

                {/* Phone Number */}
                {requirePhone && (
                    <Grid item xs={12}>
                        <TextField
                            label="Phone Number*"
                            value={formData.phone_number}
                            onChange={(e) => handleInputChange('phone_number', e.target.value)}
                            error={!!validationErrors.phone_number}
                            helperText={validationErrors.phone_number}
                            placeholder="Enter 10-digit phone number"
                            inputProps={{ maxLength: 10 }}
                            fullWidth
                        />
                    </Grid>
                )}

                {/* Email (if required) */}
                {requireEmail && (
                    <Grid item xs={12}>
                        <TextField
                            label="Email*"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={!!validationErrors.email}
                            helperText={validationErrors.email}
                            placeholder="Enter email address"
                            fullWidth
                        />
                    </Grid>
                )}

                {/* Relation (if required) */}
                {requireRelation && (
                    <Grid item xs={12}>
                        <CustomSelect
                            id="relation-select"
                            label="Select Relation*"
                            value={formData.relation}
                            onChange={(e) => handleInputChange('relation', e.target.value)}
                            error={!!validationErrors.relation}
                            helperText={validationErrors.relation}
                            fullWidth
                        >
                            <MenuItem value="" disabled>
                                Choose relation
                            </MenuItem>
                            {relations.map((relationOption, index) => (
                                <MenuItem 
                                    key={index} 
                                    value={relationOption.value || relationOption}
                                >
                                    {relationOption.label || relationOption}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Grid>
                )}

                {/* Additional Custom Fields */}
                {additionalFields.map((field, index) => (
                    <Grid item xs={12} key={index}>
                        {field.type === 'text' && (
                            <TextField
                                label={field.label}
                                value={formData[field.name] || ''}
                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                error={!!validationErrors[field.name]}
                                helperText={validationErrors[field.name]}
                                placeholder={field.placeholder}
                                required={field.required}
                                fullWidth
                            />
                        )}
                        {field.type === 'select' && (
                            <CustomSelect
                                id={`${field.name}-select`}
                                label={field.label}
                                value={formData[field.name] || ''}
                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                error={!!validationErrors[field.name]}
                                helperText={validationErrors[field.name]}
                                required={field.required}
                                fullWidth
                            >
                                <MenuItem value="" disabled>
                                    {field.placeholder || `Choose ${field.label.toLowerCase()}`}
                                </MenuItem>
                                {field.options.map((option, optIndex) => (
                                    <MenuItem 
                                        key={optIndex} 
                                        value={option.value || option}
                                    >
                                        {option.label || option}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        )}
                    </Grid>
                ))}

                {/* Confirmation Checkbox */}
                {showConfirmation && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <CustomCheckbox 
                            label={confirmationText}
                            checked={confirm}
                            onChange={(e) => {
                                setConfirm(e.target.checked);
                                if (e.target.checked && validationErrors.confirm) {
                                    setValidationErrors(prev => {
                                        const { confirm, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                        />
                        {validationErrors.confirm && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {validationErrors.confirm}
                            </Typography>
                        )}
                    </Grid>
                )}
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                {showCancelButton && (
                    <CustomButton
                        variant="outlined"
                        onClick={handleCancel}
                        label={cancelButtonText}
                        disabled={isSubmitting}
                    />
                )}
                <CustomButton
                    variant="filled"
                    onClick={handleSubmit}
                    label={saveButtonText}
                    fullWidth={!showCancelButton}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                />
            </Box>
        </Box>
    );
};

export default ReusableManualEntryForm;